import { Suspense, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMessageContext,
  createQueries,
  freshnessOf,
  isApiError,
  message,
  type AccountInfo,
  type ApiClient,
  type Session,
} from '@sl/core';
import { Button } from '../../ui/Button/Button';
import { ErrorState } from '../../ui/State/ErrorState';
import { Skeleton, SkeletonGroup } from '../../ui/Skeleton/Skeleton';
import { PeriodControlWhenGoverned } from '../period/PeriodControl';
import { PeriodProvider } from '../period/PeriodProvider';
import { AdPreviewProvider } from '../../creative/AdPreviewProvider';
import { AnalysisScopeNotice } from './AnalysisScopeNotice';
import {
  LazySearchPalette,
  SearchContext,
  SearchTrigger,
  useSearchPalettePrefetch,
  useSearchShortcuts,
  type SearchController,
} from '../../search';
import { useApiClient } from '../api/ApiClientProvider';
import { useClock } from '../clock/ClockProvider';
import { useAppConfig } from '../configContext';
import { useSavedTheme } from '../theme/useSavedTheme';
import { messageText } from '../messages';
import { testId } from '../testId';
import { AccountSwitch } from './AccountSwitch';
import { ConnectedAccountSwitch } from './ConnectedAccountSwitch';
import { rememberAccount, rememberedAccount } from './selectedAccount';
import { ConnectionBand } from './ConnectionBand';
import { StagedProvider } from '../staged/stagedContext';
import { StagedPill, StagedReminder } from '../staged/StagedUi';
import { StagedTray, TrayProvider } from '../staged/StagedTray';
import { Freshness } from './Freshness';
import { LiveStates } from './LiveStates';
import { ShellLiveContext, useShellLive } from './useShellLive';
import { HelpMenu } from './HelpMenu';
import { ModeChip, SurfaceModeChip } from './ModeChip';
import { ModeMenu } from './ModeMenu';
import { ShellNav, ShellTabBar } from './ShellNav';
import { ViewOnlyNotice } from './ViewOnlyNotice';
import { WorkspaceWelcome } from './WorkspaceWelcome';
import { useAccountDayRollover } from './useAccountDayRollover';
import { SHELL_COPY } from './copy';
import { ShellContext, type ShellContextValue } from './shellContext';
import { usePhoneLayout } from './useBreakpoint';
import styles from './AppShell.module.css';

// The global shell (T-036) — the layout route of every authenticated screen (`shell: true` in
// routes.table.ts). Welcome and the first-run flow stay outside it (REQ-006 AC5).
//
// **What this file may import** (T-321 / BUG-155): everything it reaches STATICALLY is in the app's first
// chunk, so ARCH §8.1's rule for the shell is "only what the first paint needs" — a trigger in the top bar
// belongs here, the panel behind it does not. `app/shell/bundle.test.ts` is the guard, and it names the four
// surfaces that are behind a press: the command palette, the review dialog, the date panel and its calendar.
//
// What it owns: the navigation, the account name, the mode chip and its menu (REQ-006), the freshness label
// and the refresh control (REQ-283), the skip link, and the account-shaped context every screen below it
// reads (`useShell()`). What it does NOT own: the date range and the compare switch (T-037), the search field
// and the staged-changes tray (later tasks) — their places in the top bar are marked.
//
// Layering (SC-6): the top bar is `position: sticky; top: 0` at `--z-sticky` (100), so the mode chip is still
// on screen after any amount of scrolling; every layer that can cover it — popover and menu (500), modal
// (700), toast (800) — sits above it and either leaves it visible (a popover is anchored and small) or
// repeats it in its own header (`Modal.modeChip`, REQ-006 AC5).

/** The account the shell shows: the session's default, or the first one it lists. */
export function shellAccount(session: Session): AccountInfo | undefined {
  return session.accounts.find((a) => a.id === session.defaultAccountId) ?? session.accounts[0];
}

function ShellSkeleton() {
  return (
    <div className={styles.app} data-shell="loading">
      <div className={styles.main}>
        <header className={styles.topbar}>
          <SkeletonGroup
            label="Loading your account"
            testID={testId('state', 'loading')}
            className={styles.topbarLeft}
          >
            <Skeleton shape="pill" width={160} />
            <Skeleton shape="pill" width={110} />
          </SkeletonGroup>
        </header>
        <div className={styles.page} />
      </div>
    </div>
  );
}

/**
 * The shell could not open. **BUG-016's web half**: when the client says the DEVICE has no connection, this
 * is not an error of ours and must not read like one (REQ-246, REQ-287 AC1's "offline" state). The band that
 * every other screen shows over its data — same words, same `cloud-off`, same `state.offline-banner` id — is
 * what stands in for the page here, with the retry under it. `?demoFault=offline` rejects every call, so
 * before this the one thing an owner on a train saw was "We couldn't open your account".
 */
function ShellError({ error, offline = false }: { error: unknown; offline?: boolean }) {
  const sentence = isApiError(error) ? error.userMessage : message('error.unknown');
  // T-076-W5's rule, the phone's too (T-239 (b)): `<its error state's id>.retry`.
  const retry = (
    <Button
      variant="primary"
      testID={
        offline ? testId('state', 'offline-banner', 'retry') : testId('state', 'error', 'retry')
      }
      onClick={() => window.location.reload()}
    >
      {SHELL_COPY.retry}
    </Button>
  );
  return (
    <div className={styles.app} data-shell={offline ? 'offline' : 'error'}>
      <div className={styles.main}>
        <main className={styles.page}>
          {offline ? (
            // The same news the band gives over data, filling the page because there is no data to put it
            // over — same id, same `cloud-off`, same sentence. It is an `ErrorState` rather than a `Banner`
            // because a band is laid out INSIDE a page and this one IS the page; the words are what the
            // requirement is about, and they are the band's.
            <ErrorState
              icon="cloud-off"
              title={SHELL_COPY.offlineTitle}
              body={SHELL_COPY.offlineBody}
              testID={testId('state', 'offline-banner')}
              action={retry}
            />
          ) : (
            <ErrorState
              title="We couldn't open your account"
              body={messageText(sentence)}
              action={retry}
            />
          )}
        </main>
      </div>
    </div>
  );
}

interface ShellFrameProps {
  session: Session;
  account: AccountInfo;
  client: ApiClient;
  onSelect: (id: string) => void;
  onConnected: (id: string) => Promise<void>;
}

function ShellFrame({ session, account, client, onSelect, onConnected }: ShellFrameProps) {
  const phone = usePhoneLayout();
  // How this build runs, read ONCE from the address in `main.tsx` (T-076-W7's rule: nothing below re-reads it).
  const config = useAppConfig();
  // The injected clock (D-020), never `new Date()` — `?demoNow=` is what moves the 24-hour reminder.
  const clock = useClock();
  const mainRef = useRef<HTMLElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLButtonElement>(null);
  const [modeOpen, setModeOpen] = useState(false);
  const menuId = useId();
  // Global search (T-044, REQ-042 AC1). The shell owns the open state because two affordances reach one
  // surface: the trigger in the top bar, and — below 768 px, where the bar has no room for words — the row
  // in the phone's More sheet. `/` and Ctrl/Cmd+K are one document listener, armed only while it is shut.
  const [searchOpen, setSearchOpen] = useState(false);
  const paletteId = useId();
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  useSearchShortcuts(!searchOpen, openSearch);
  // T-049: the palette is its own chunk now, so `/` must not wait for a network round trip — the chunk is
  // asked for once the shell is idle, long before anyone reaches for the key (T-044's note (1)).
  useSearchPalettePrefetch();
  const searchController = useMemo<SearchController>(
    () => ({ open: searchOpen, openSearch, closeSearch }),
    [searchOpen, openSearch, closeSearch],
  );
  // REQ-287: losing the connection is the user's situation, not an error of ours — one band, on every
  // screen, until it comes back (w17 §4). The client is the only thing that knows; it tells us when it
  // changes, so nothing polls.
  const [offline, setOffline] = useState(() => client.connectivity() === 'offline');
  useEffect(
    () => client.onConnectivityChange((state) => setOffline(state === 'offline')),
    [client],
  );

  const value = useMemo<ShellContextValue>(() => {
    const deviceTimeZone = deviceZone();
    const locale = appLocale();
    return {
      session,
      account,
      queries: createQueries(client, { freshness: freshnessOf(account) }),
      messages: createMessageContext(
        {
          account: { currency: account.currency, timeZone: account.timezone },
          locale,
          deviceTimeZone,
        },
        { strict: import.meta.env.DEV },
      ),
      locale,
      deviceTimeZone,
    };
  }, [session, account, client]);

  // T-211 — the shell's ONE read of the answer's `meta` (D-1078's whole point), resolved here because three
  // surfaces below say something about it: the caption and its control in the bar, §12.2's band, and
  // REQ-002 AC2 / REQ-283 AC2's own banners. Two readers deriving it a millisecond apart could disagree
  // about API §1.6's sixty-minute line, so there is one.
  const live = useShellLive({ shell: value, account, client, offline });

  // The skip link cannot be an `href="#main"`: this app routes on the hash, so a fragment link would look
  // like a route change. It moves focus itself (REQ-281).
  const skipToContent = useCallback(() => {
    const main = mainRef.current;
    if (!main) return;
    main.focus({ preventScroll: false });
    main.scrollIntoView?.({ block: 'start' });
  }, []);

  return (
    <ShellContext.Provider value={value}>
      {/* REQ-282 AC2: a browser with no local override adopts the appearance saved with the account. */}
      <SavedTheme />
      {/* T-129 / ARCH §9.3 — the open change-set is read ONCE, here, and every screen and overlay below
          takes the markers and the count out of the context. Inside the shell context because the query is
          keyed on the account; outside `PeriodProvider` because the tray is the tray whatever dates are
          showing (the query is deliberately not period-keyed). */}
      <StagedProvider>
        {/* T-132 — one open state for the tray, shared by the pill that anchors it and the reminder band
          that also opens it, inside the staged context it reads and above every screen it covers. */}
        <TrayProvider>
          {/* The date range wraps the top bar AND the page, so the control and every number below it read one
          range (REQ-021 AC4). Screens take it from `usePeriod()`, never from the URL. */}
          <PeriodProvider>
            <AdPreviewProvider>
              <SearchContext.Provider value={searchController}>
                <div className={styles.app} data-shell={phone ? 'phone' : 'desktop'}>
                  <button
                    type="button"
                    className={styles.skipLink}
                    data-testid={testId('shell', 'skip-link')}
                    onClick={skipToContent}
                  >
                    {SHELL_COPY.skipLink}
                  </button>

                  {phone ? null : <ShellNav account={account} user={session.user} />}

                  <div className={styles.main}>
                    <header className={styles.topbar} data-testid={testId('shell', 'topbar')}>
                      <div className={styles.topbarLeft}>
                        {/* API builds switch server-listed accounts and open guided attachment. Demo builds
                          retain the separate pulled-dataset selector; they never discover a real server. */}
                        {config.apiBuild ? (
                          <ConnectedAccountSwitch
                            account={account}
                            initials={initialsOf(account.name)}
                            session={session}
                            onSelect={onSelect}
                            onConnected={onConnected}
                          />
                        ) : (
                          <AccountSwitch account={account} initials={initialsOf(account.name)} />
                        )}
                        <ModeChip
                          ref={chipRef}
                          mode={account.mode}
                          messages={value.messages}
                          open={modeOpen}
                          menuId={menuId}
                          triggerLabel={SHELL_COPY.modeChipTrigger}
                          onToggle={() => setModeOpen((was) => !was)}
                        />
                        {/* REQ-101 AC4 — the count is visible wherever you are, beside the thing that says
                      whose account this is. Hidden at zero. */}
                        <StagedPill />
                        {/* The panel portals to the end of <body>; it lives beside its anchor so the two are
                      read together. REQ-101 AC2, AC6. */}
                        <StagedTray />
                        <Freshness account={account} live={live} />
                      </div>
                      {/* DESIGN_SYSTEM §10's right-hand slot, in its order: search, then the date range and the
                  compare switch. Search is the first thing to give way (Search.module.css). */}
                      <div className={styles.topbarRight}>
                        <SearchTrigger
                          ref={searchRef}
                          open={searchOpen}
                          onOpen={openSearch}
                          paletteId={paletteId}
                        />
                        <PeriodControlWhenGoverned />
                        {/* The mockups' own last control (`_shared/web-shell.html`), real since T-177: it is
                          where REQ-005 AC1 says the first-run cards are re-opened from. */}
                        <HelpMenu />
                      </div>
                    </header>

                    <main
                      ref={mainRef}
                      id="sl-main"
                      tabIndex={-1}
                      className={styles.page}
                      data-testid={testId('shell', 'page')}
                    >
                      {/* REQ-264 AC2: an account-level problem is said ONCE, here, on every screen — never per row.
                  It is the first thing inside `<main>`, so the skip link lands on it rather than past it. */}
                      <ConnectionBand
                        account={account}
                        offline={offline}
                        rateLimited={live.state.rateLimited}
                        offlineMessage={live.state.offline.message}
                      />
                      {/* T-211 — the two live-data states that need a page and not a caption: REQ-002 AC2's
                    first-sync progress and REQ-283 AC2's three-hour banner (w17 §5, §6). Under the band for
                    the same reason the notice is: a problem we cannot read past outranks a fact about how
                    old the reading is. Both are absent on a healthy account and on every demo account
                    without an armed fault. */}
                      <LiveStates live={live} />
                      {/* DESIGN_SYSTEM §12.2's one-time banner on a live, view-only account (T-210) — under the
                    band because a problem outranks a fact, and absent on every demo account. */}
                      <ViewOnlyNotice account={account} client={client} />
                      {/* REQ-101 AC5 — one gentle reminder on the next visit, under the account-level band and
                    above the screen, so it is read once per visit rather than once per screen. */}
                      <StagedReminder now={clock.now()} timeZone={account.timezone} />
                      {/* T-428 (on T-422's note (5), D-1115 (f)) — the shell's ONE live state, handed to the
                    ROUTE as well as to the three surfaces above, which take it as a prop. A route arrives
                    through `<Outlet/>` and had no way to read it, so Home re-observed `queries.syncStatus`
                    to learn a fact already resolved here; the phone has had this provider since T-214. It
                    wraps the outlet alone: nothing above it needs the context, because everything above it
                    is handed the value directly. */}
                      <ShellLiveContext.Provider value={live}>
                        <AnalysisScopeNotice />
                        <Outlet />
                      </ShellLiveContext.Provider>
                    </main>
                  </div>

                  {phone ? <ShellTabBar account={account} user={session.user} /> : null}
                </div>

                {/* REQ-042 AC1. Inside `PeriodProvider` because a result carries the range in force onto the
              entity page, and inside the shell context because the index is read per account. */}
                <LazySearchPalette
                  open={searchOpen}
                  onClose={closeSearch}
                  paletteId={paletteId}
                  /* REQ-006 AC5 / BUG-009: at 360 px the palette is the whole screen (Search.module.css). */
                  modeChip={<SurfaceModeChip testID={testId('search', 'palette', 'mode-chip')} />}
                />
              </SearchContext.Provider>
            </AdPreviewProvider>
          </PeriodProvider>

          <ModeMenu
            open={modeOpen}
            onClose={() => setModeOpen(false)}
            anchorRef={chipRef}
            menuId={menuId}
            account={account}
            client={client}
          />
        </TrayProvider>
      </StagedProvider>
    </ShellContext.Provider>
  );
}

/** Nothing to draw: it only applies the account's saved appearance on a browser that has none of its own. */
function SavedTheme() {
  useSavedTheme();
  return null;
}

function ShellBody() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(rememberedAccount);
  // Before the account is known the transport decides the stale time (demo 60 s) — T-023's rule.
  const bootstrap = useMemo(() => createQueries(client), [client]);
  // D-589 — the ONE query in the app that refetches on focus. `account.today` is now what every preset is
  // resolved against (`PeriodProvider`), and it rides on this read: a tab left open across midnight, or over
  // the night a new pull lands, has to pick the new day up without a reload. The app-wide default stays off
  // (`app/query/queryClient.ts`) because refetching numbers on focus is flicker, not news — this is one date.
  const session = useQuery({ ...bootstrap.session(), retry: false, refetchOnWindowFocus: true });
  const refreshAccountDay = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: bootstrap.session().queryKey });
  }, [queryClient, bootstrap]);
  useAccountDayRollover(session.data?.data.accounts ?? [], refreshAccountDay);

  const selectAccount = useCallback(
    (id: string) => {
      // Home and an empty URL prevent an entity ID or filters from leaking into the next account.
      rememberAccount(id);
      navigate('/home', { replace: true });
      setSelected(id);
    },
    [navigate],
  );
  const connectedAccount = useCallback(
    async (id: string) => {
      await queryClient.invalidateQueries({
        queryKey: bootstrap.session().queryKey,
        refetchType: 'none',
      });
      const fresh = await queryClient.fetchQuery({ ...bootstrap.session(), staleTime: 0 });
      if (!fresh.data.accounts.some((item) => item.id === id))
        throw new Error(
          'The account is attached, but its dashboard is still opening. Please try again.',
        );
      selectAccount(id);
    },
    [bootstrap, queryClient, selectAccount],
  );

  if (session.isPending) return <ShellSkeleton />;
  if (session.isError && client.transport === 'http' && isApiError(session.error)) {
    if (session.error.status === 401) return <Navigate to="/login" replace />;
    if (session.error.status === 403) return <Navigate to="/account" replace />;
  }
  // The client is the only thing that knows whether the device is offline (REQ-246 AC5: a demo account
  // ignores real connectivity and answers the QA switch instead), so the reason is read from it here too.
  if (session.isError)
    return <ShellError error={session.error} offline={client.connectivity() === 'offline'} />;

  const account =
    session.data.data.accounts.find((item) => item.id === selected) ??
    shellAccount(session.data.data);
  // No account to show: back to the welcome page, which is where an account comes from (REQ-002).
  if (!account) {
    if (client.transport === 'http' && /^usr_[0-9a-f]{32}$/.test(session.data.data.user.id))
      return <WorkspaceWelcome displayName={session.data.data.user.displayName} />;
    return <Navigate to={client.transport === 'http' ? '/account' : '/'} replace />;
  }

  // Recreate ALL account-scoped providers, overlays and local component state on a switch. Cached
  // server responses already include the account key; no previous account's chart can become a placeholder.
  return (
    <ShellFrame
      key={account.id}
      session={session.data.data}
      account={account}
      client={client}
      onSelect={selectAccount}
      onConnected={connectedAccount}
    />
  );
}

/**
 * What the data router shows while the first lazy screen chunk is still arriving — the same skeleton the
 * shell shows while its own session request is in flight, so the page does not change shape twice.
 */
export function ShellFallback() {
  return <ShellSkeleton />;
}

/** The layout route: `<AppShell>` wraps every `shell: true` route (app/router.tsx). */
export function AppShell() {
  return (
    <Suspense fallback={<ShellSkeleton />}>
      <ShellBody />
    </Suspense>
  );
}

/** "Juniper Home Goods" → "JH". Decorative: the name itself is beside it. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.slice(0, 2).map((word) => word[0] ?? '');
  return letters.join('').toUpperCase() || '?';
}

function appLocale(): string {
  return typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';
}

function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
